<template>
  <div class="w-100 chart-wrapper">
    <fusioncharts
      type="multiaxisline"
      width="100%"
      :height="height"
      dataFormat="json"
      :dataSource="dataSource"
    ></fusioncharts>
  </div>
</template>

<script>
import Vue from "vue";
import Vuex, { mapGetters } from "vuex";
import VueFusionCharts from "vue-fusioncharts";
import FusionCharts from "fusioncharts";
import PowerCharts from "fusioncharts/fusioncharts.powercharts";
import Zoomline from "fusioncharts/fusioncharts.zoomline";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";

Vue.use(VueFusionCharts, FusionCharts, PowerCharts, Zoomline, FusionTheme);

export default {
  props: {
    stats: Array,
    tempGraph: Boolean,
    humiGraph: Boolean,
    title: {
      default: "Chart",
      type: String,
    },
    height: {
      default: "470",
      type: String,
    },
    deviceName: String,
    hasLight: Boolean,
    hasTemp2: Boolean,
  },
  name: "MultiAxisChart",
  data() {
    return {
      dataSource: {},
    };
  },
  computed: {
    ...mapGetters(["profile"]),
  },
  watch: {
    stats: {
      handler: function () {
        this.renderChart();
      },
      deep: true,
      immediate: true,
    },
    profile: {
      handler: function () {
        this.renderChart();
      },
      deep: true,
    },
  },
  methods: {
    renderChart() {
      if (!this.stats || this.stats.length === 0) {
        // No data scenario
        const emptyDataSource = {
          chart: {
            caption: this.deviceName || this.title,
            subcaption: this.$t("sub_cap"),
            theme: "fusion",
            nodatamessage: "No data available for this period.",
          },
        };

        this.dataSource = emptyDataSource;
        return;
      }

      // Normal rendering
      const labels = [];
      const lightData = [];
      const tempData = [];
      const minTempData = [];
      const maxTempData = [];
      const humiData = [];
      const minHumiData = [];
      const maxHumiData = [];
      const temp2Data = [];
      const vibrationData = [];

      this.stats.forEach((stat) => {
        const date = new Date(stat.deviceTimeStamp).toLocaleString();
        labels.push({ label: date });

        if (this.hasLight) {
          lightData.push({ value: stat.light });
        }
        if (this.hasTemp2) {
          temp2Data.push({ value: stat.temperature2 });
        }
        tempData.push({ value: stat.temperature });
        minTempData.push({ value: stat.deviceSettings.minTemperature });
        maxTempData.push({ value: stat.deviceSettings.maxTemperature });
        humiData.push({ value: stat.humidity });
        minHumiData.push({ value: stat.deviceSettings.minHumidity });
        maxHumiData.push({ value: stat.deviceSettings.maxHumidity });

        if (stat.vibration !== undefined && stat.vibration !== null) {
          vibrationData.push({ value: stat.vibration });
        }
      });

      const hasValidData = (field) => {
        return this.stats && this.stats.some((s) => s[field] !== undefined && s[field] !== null);
      };

      // Construct maps of all possible axes
      const axisMap = {
        temperature: hasValidData("temperature")
          ? {
              title: "Temperature",
              titlepos: "left",
              axisonleft: "1",
              yaxisname: "Temperature (°C)",
              color: "#2196F3",
              dataset: [
                {
                  seriesname: "Temperature",
                  color: "#2196F3",
                  data: tempData,
                  showlegend: "1",
                  plottooltext: "Temperature: $dataValue °C",
                  legendIconBorderColor: "#2196F3",
                  legendIconBgColor: "#2196F3",
                },
                {
                  seriesname: "Min Temp",
                  color: "#1976D2",
                  data: minTempData,
                  showlegend: "1",
                  plottooltext: "Min Temp: $dataValue °C",
                  legendIconBorderColor: "#1976D2",
                  legendIconBgColor: "#1976D2",
                },
                {
                  seriesname: "Max Temp",
                  color: "#0D47A1",
                  data: maxTempData,
                  showlegend: "1",
                  plottooltext: "Max Temp: $dataValue °C",
                  legendIconBorderColor: "#0D47A1",
                  legendIconBgColor: "#0D47A1",
                },
              ],
            }
          : null,
        humidity: hasValidData("humidity")
          ? {
              title: "Humidity",
              titlepos: "right",
              axisonleft: "0",
              yaxisname: "Humidity (%)",
              color: "#4CAF50",
              dataset: [
                {
                  seriesname: "Humidity",
                  color: "#4CAF50",
                  data: humiData,
                  showlegend: "1",
                  plottooltext: "Humidity: $dataValue %",
                  legendIconBorderColor: "#4CAF50",
                  legendIconBgColor: "#4CAF50",
                },
                {
                  seriesname: "Min Humidity",
                  color: "#388E3C",
                  data: minHumiData,
                  showlegend: "1",
                  plottooltext: "Min Humidity: $dataValue %",
                  legendIconBorderColor: "#388E3C",
                  legendIconBgColor: "#388E3C",
                },
                {
                  seriesname: "Max Humidity",
                  color: "#2E7D32",
                  data: maxHumiData,
                  showlegend: "1",
                  plottooltext: "Max Humidity: $dataValue %",
                  legendIconBorderColor: "#2E7D32",
                  legendIconBgColor: "#2E7D32",
                },
              ],
            }
          : null,
        light: (this.hasLight && hasValidData("light"))
          ? {
              title: "Light",
              titlepos: "left",
              axisonleft: "1",
              yaxisname: "Light Intensity",
              color: "#FFEB3B",
              dataset: [
                {
                  seriesname: "Light Intensity",
                  color: "#FFEB3B",
                  data: lightData,
                  showlegend: "1",
                  plottooltext: "Light Intensity: $dataValue",
                  legendIconBorderColor: "#FFEB3B",
                  legendIconBgColor: "#FFEB3B",
                },
              ],
            }
          : null,
        vibration: hasValidData("vibration")
          ? {
              title: "Vibration",
              titlepos: "right",
              axisonleft: "0",
              yaxisname: "Vibration",
              color: "#9C27B0",
              dataset: [
                {
                  seriesname: "Vibration",
                  color: "#9C27B0",
                  data: vibrationData,
                  showlegend: "1",
                  plottooltext: "Vibration: $dataValue",
                  legendIconBorderColor: "#9C27B0",
                  legendIconBgColor: "#9C27B0",
                },
              ],
            }
          : null,
        temperature2: (this.hasTemp2 && hasValidData("temperature2"))
          ? {
              title: "Temperature - Ambient",
              titlepos: "left",
              axisonleft: "1",
              yaxisname: "Temperature - Ambient (°C)",
              color: "#F44336",
              dataset: [
                {
                  seriesname: "Temperature (Amb)",
                  color: "#F44336",
                  data: temp2Data,
                  showlegend: "1",
                  plottooltext: "Temperature - Ambient: $dataValue °C",
                  legendIconBorderColor: "#F44336",
                  legendIconBgColor: "#F44336",
                },
              ],
            }
          : null,
      };

      // Arrange axes based on user settings
      let axes = [];
      const chartFields =
        this.profile?.company?.chartFields ||
        this.profile?.company?.profile?.chartFields;

      if (chartFields && Array.isArray(chartFields) && chartFields.length > 0) {
        // Sort by 'order' ascending
        const sortedFields = [...chartFields].sort((a, b) => a.order - b.order);

        sortedFields.forEach((field) => {
          const key = field.key;
          if (axisMap[key]) {
            axes.push(axisMap[key]);

            // Group ambient temperature alongside main temperature
            if (key === "temperature" && axisMap.temperature2) {
              axes.push(axisMap.temperature2);
            }
          }
        });

        // Ensure fallback for Temperature if it was somehow omitted
        if (!axes.some((axis) => axis.title === "Temperature")) {
          if (axisMap.temperature) {
            axes.unshift(axisMap.temperature);
          }
          if (axisMap.temperature2) {
            const insertIndex = axisMap.temperature ? 1 : 0;
            axes.splice(insertIndex, 0, axisMap.temperature2);
          }
        }
      } else {
        // Default ordering fallback
        if (axisMap.light) axes.push(axisMap.light);
        if (axisMap.temperature2) axes.push(axisMap.temperature2);
        if (axisMap.temperature) axes.push(axisMap.temperature);
        if (axisMap.humidity) axes.push(axisMap.humidity);
        if (axisMap.vibration) axes.push(axisMap.vibration);
      }

      const dataSource = {
        chart: {
          caption: this.deviceName,
          subcaption: this.$t("sub_cap"),
          showvalues: "0",
          theme: "fusion",
          exportEnabled: "1",
          exportFileName: this.title,
          showlegend: "1",
          showHoverEffect: "1",
        },
        categories: [{ category: labels }],
        axis: axes,
      };

      this.dataSource = dataSource;
    },
  },
};
</script>

<style scoped>
.w-100 {
  width: 100%;
}
.chart-wrapper {
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
}
</style>
